import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { PageLayout } from 'components/PageLayout';
import { API } from 'api';
import { InlineStatus } from 'components/InlineStatus';
import { useAuthState } from 'state/useAuthState';
import { useEnableFlightTrackerLayer } from 'features/mapping/hooks/useEnableFlightTrackerLayer';
import { registerDeviceWithWatchDuty } from 'shared/device';
import { useIapState } from 'features/payments/hooks/useIapState';
import { VerifyPaymentData } from 'shared/types';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { usePostHog } from 'posthog-js/react';
import { useMembershipAccountInfoQueryParams } from 'features/membership/hooks/useMembershipUrlParams';
import { Loader } from 'components/Loader';
import * as Sentry from '@sentry/react';

const verifyPurchaseApi = async (data: {
  clientToken: string;
}): Promise<VerifyPaymentData> => {
  const response = await API.post<
    { clientToken: string },
    AxiosResponse<VerifyPaymentData>
  >('recurly_integration/payment_verification/', data);
  return response.data;
};

const useStyles = makeStyles()(() => ({
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const Content = (): JSX.Element => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { isAuthenticated, loginSuccess } = useAuthState();
  const { enableFlightTrackerLayer } = useEnableFlightTrackerLayer();
  const { resetIapState } = useIapState();
  const posthog = usePostHog();

  const { clientToken, upgrade: isMembershipUpgrade } =
    useMembershipAccountInfoQueryParams();

  const {
    mutate: verifyPurchase,
    isPending: verifyingPurchase,
    error,
  } = useMutation({
    mutationFn: verifyPurchaseApi,
    onSuccess: (data) => {
      resetIapState();
      loginSuccess(data.key, data.user);
      enableFlightTrackerLayer();
      posthog.capture('membership_success_login');
      logEvent({
        name: ANALYTICS_EVENTS.SUPPORT.SUCCESS_MEMBERSHIP,
        params: {},
      });
      // associate user with token
      const params = { user: data.user };
      registerDeviceWithWatchDuty(params);
    },
    onError: (err) => {
      Sentry.captureException(err, {
        extra: { context: 'iap-payment-verification', clientToken },
      });
    },
  });

  useEffect(() => {
    if (!isAuthenticated && clientToken) {
      verifyPurchase({ clientToken });
      // remove query params
      window.history.replaceState({}, document.title, '/');
    }
  }, [clientToken, isAuthenticated, verifyPurchase]);

  useEffect(() => {
    if (!clientToken && isAuthenticated) {
      resetIapState();
      enableFlightTrackerLayer();
      persistedQueryClient.invalidateQueries({ queryKey: ['auth-user'] });
    }
  }, [clientToken, enableFlightTrackerLayer, resetIapState, isAuthenticated]);

  if (verifyingPurchase) {
    return <Loader disableBackground />;
  }

  if (error) {
    return <InlineStatus status="error" fullWidth />;
  }

  return (
    <>
      <Helmet>
        <title>{t('membershipSuccess.title')}</title>
      </Helmet>

      <SimplePageLayout>
        <Box className={classes.contentContainer}>
          <Typography variant="h2" align="center" sx={{ mb: 4 }}>
            <b>{t('membershipSuccess.subtitle')}</b>
          </Typography>

          <Typography align="center" sx={{ mb: 3 }}>
            {t(
              `membershipSuccess.thanks.${
                isMembershipUpgrade ? 'pro' : 'regular'
              }`,
            )}
          </Typography>

          {Boolean(clientToken) && (
            <Typography align="center">
              {t('membershipSuccess.message')}
            </Typography>
          )}
        </Box>

        <Button
          fullWidth
          size="large"
          sx={{ mt: 2 }}
          component={Link}
          to={
            isMembershipUpgrade
              ? '/?membershipUpgrade=true'
              : '/?membershipSuccess=true'
          }
          replace
        >
          {t('membershipSuccess.button')}
        </Button>
      </SimplePageLayout>
    </>
  );
};

export const MembershipIapSuccess = (): JSX.Element => {
  return (
    <PageLayout
      data-cname="MembershipIapSuccessPage"
      allowAppBanner={false}
      headerBarProps={{ action: 'noControls' }}
    >
      <Content />
    </PageLayout>
  );
};
